import { useState, useEffect, useMemo, useRef } from 'react'
import {
  Table,
  Dropdown,
  Button,
  Space,
  Typography,
  Card,
  Input,
  Modal,
  Form,
  InputNumber,
  Select,
  message,
  Row,
  Col,
  Statistic,
  Tag,
  Upload,
  Divider,
  List,
  Drawer,
  FloatButton,
  Badge,
  Grid,
  DatePicker,
  Image,
  Tooltip,
  Progress,
  Spin
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
  DollarOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  DownloadOutlined,
  FilterOutlined,
  FileTextOutlined,
  FileOutlined,
  CalendarOutlined,
  ShopOutlined,
  MoneyCollectOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  InboxOutlined,
  PrinterOutlined,
  BookOutlined,
  MoreOutlined,
  CameraOutlined
} from '@ant-design/icons'
import { generateExpensesTablePDF } from '../../services/expenseTablePdfService'
import dayjs from 'dayjs'
import expenseService from '../../services/expenseService'
import contactService from '../../services/contactService'
import { useProjectsOptions } from '../../hooks/useProjects'
import FileViewer from '../../components/FileViewer'
import { useAuth } from '../../contexts/AuthContext'
import { generateExpensePDF } from '../../services/expensePdfService'
import { generateExpenseCertificatePDF } from '../../services/expenseCertificatePdfService'
import { getCompanySettings, getLogoUrl, updateSignatureLayout } from '../../services/settingsService'
import { getContactById } from '../../services/contactService'
import { buildAssetUrl } from '../../config/runtimeConfig'
import AccountingPostingPreviewModal from '../../components/AccountingPostingPreviewModal'
import DocumentPresetQuickSelect from '../../components/DocumentPresetQuickSelect'
import SignatureLayoutControls from '../../components/SignatureLayoutControls'
import MobilePdfPreview from '../Receipts/MobilePdfPreview'
import { getCompanySignatureLayout, SIGNATURE_LAYOUT_DEFAULTS } from '../../utils/signatureLayout'
import './ExpensesPage.mobile.css'
import './ExpensePrintModal.css'
// import { PrinterOutlined } from '@ant-design/icons'

// Utility function to convert relative file URL to absolute URL
const getFileUrl = (relativeUrl) => {
  if (!relativeUrl) return null
  if (relativeUrl.startsWith('http')) return relativeUrl
  return buildAssetUrl(relativeUrl)
}

const { Title, Text } = Typography
const { Search } = Input
const { Option } = Select
const { RangePicker } = DatePicker
const { Dragger } = Upload
const { useBreakpoint } = Grid

const EXPENSE_PRINT_OPTIONS_KEY = 'EXPENSE_PRINT_OPTIONS'
const EXPENSE_PRINT_OPTIONS_DEFAULTS = {
  recorder_mode: 'current_user',
  recorder_custom_name: ''
}

const loadExpensePrintOptions = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(EXPENSE_PRINT_OPTIONS_KEY) || '{}')
    return { ...EXPENSE_PRINT_OPTIONS_DEFAULTS, ...stored }
  } catch {
    return { ...EXPENSE_PRINT_OPTIONS_DEFAULTS }
  }
}

const ExpensesPage = () => {

  const { user, hasRole, hasPermission } = useAuth()
  const isSuperAdmin = hasRole('SUPER_ADMIN')
  const canCreateSupplier = hasPermission('contacts', 'create')
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [editingExpense, setEditingExpense] = useState(null)
  const [viewingExpense, setViewingExpense] = useState(null)
  const [postingExpense, setPostingExpense] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedSupplier, setSelectedSupplier] = useState('all')
  const [selectedProject, setSelectedProject] = useState('all')
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [form] = Form.useForm()
  const [supplierForm] = Form.useForm()
  const [suppliers, setSuppliers] = useState([])
  const [supplierModalVisible, setSupplierModalVisible] = useState(false)
  const [supplierSaving, setSupplierSaving] = useState(false)
  const [categories, setCategories] = useState([])
  const [defaultCategories, setDefaultCategories] = useState([])
  const [stats, setStats] = useState({
    total_amount: 0, expense_before_vat: 0, input_vat: 0,
    approved_amount: 0, pending_amount: 0, total_expenses: 0,
    posted_expense_before_vat: 0, posted_document_total: 0, unposted_document_total: 0
  })
  const [fileList, setFileList] = useState([])
  const [uploading, setUploading] = useState(false)
  const [attachmentPreview, setAttachmentPreview] = useState(null)
  const cameraInputRefs = useRef({})
  const fileInputRefs = useRef({})

  // PDF Modal state
  const [pdfModalVisible, setPdfModalVisible] = useState(false)
  const [pdfDataUrl, setPdfDataUrl] = useState('')
  const [pdfObjectUrl, setPdfObjectUrl] = useState('')
  const [mobilePrintTab, setMobilePrintTab] = useState('preview')
  const [pdfPreviewKind, setPdfPreviewKind] = useState('expense')
  const [company, setCompany] = useState(null)
  const [pdfPreviewExpense, setPdfPreviewExpense] = useState(null)
  const [pdfPreviewSupplier, setPdfPreviewSupplier] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [certificateSigners, setCertificateSigners] = useState({ claimant_name: '', approver_name: '' })
  const [certificateSignersSaving, setCertificateSignersSaving] = useState(false)
  const certificateSignersDirty = pdfPreviewKind === 'certificate' && (
    certificateSigners.claimant_name !== (pdfPreviewExpense?.certificate_claimant_name ?? pdfPreviewExpense?.created_by_name ?? user?.name ?? '') ||
    certificateSigners.approver_name !== (pdfPreviewExpense?.certificate_approver_name ?? pdfPreviewExpense?.approved_by_name ?? '')
  )
  const [signatureLayout, setSignatureLayout] = useState({ ...SIGNATURE_LAYOUT_DEFAULTS.expense })
  const [signatureLayoutSaving, setSignatureLayoutSaving] = useState(false)
  const [expensePrintOptions, setExpensePrintOptions] = useState(loadExpensePrintOptions)

  useEffect(() => {
    if (!pdfDataUrl) {
      setPdfObjectUrl('')
      return undefined
    }
    try {
      const base64 = pdfDataUrl.split(',')[1]
      const bytes = Uint8Array.from(atob(base64), character => character.charCodeAt(0))
      const objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      setPdfObjectUrl(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    } catch (error) {
      console.error('Expense PDF object URL error:', error)
      setPdfObjectUrl('')
      return undefined
    }
  }, [pdfDataUrl])

  const handleOpenExpensePdf = () => {
    if (certificateSignersDirty) return
    if (pdfObjectUrl || pdfDataUrl) window.open(pdfObjectUrl || pdfDataUrl, '_blank', 'noopener,noreferrer')
  }

  useEffect(() => {
    if (company) setSignatureLayout(getCompanySignatureLayout(company, 'expense'))
  }, [company])

  useEffect(() => {
    if (!pdfModalVisible || !['expense', 'certificate'].includes(pdfPreviewKind) || !pdfPreviewExpense) return undefined
    let active = true
    const timer = setTimeout(async () => {
      setPdfLoading(true)
      try {
        const recorderName = expensePrintOptions.recorder_mode === 'current_user'
          ? (user?.name || user?.email || '')
          : expensePrintOptions.recorder_custom_name.trim()
        const dataUrl = pdfPreviewKind === 'certificate'
          ? await generateExpenseCertificatePDF(
              pdfPreviewExpense,
              company || {},
              pdfPreviewSupplier,
              { recorder_name: recorderName, ...certificateSigners }
            )
          : await generateExpensePDF(
              pdfPreviewExpense,
              company || {},
              pdfPreviewSupplier,
              signatureLayout,
              { recorder_name: recorderName }
            )
        if (active) setPdfDataUrl(dataUrl)
      } catch (error) {
        if (active) message.error('ไม่สามารถอัปเดตตัวอย่าง PDF ได้: ' + error.message)
      } finally {
        if (active) setPdfLoading(false)
      }
    }, 180)
    return () => { active = false; clearTimeout(timer) }
  }, [pdfModalVisible, pdfPreviewKind, pdfPreviewExpense, pdfPreviewSupplier, company, signatureLayout, expensePrintOptions, certificateSigners, user])

  const updateExpensePrintOptions = (changes) => {
    setExpensePrintOptions(current => {
      const next = { ...current, ...changes }
      try {
        localStorage.setItem(EXPENSE_PRINT_OPTIONS_KEY, JSON.stringify(next))
      } catch {
        // The preview still works when browser storage is unavailable.
      }
      return next
    })
  }

  const handleSaveSignatureLayout = async () => {
    setSignatureLayoutSaving(true)
    try {
      const response = await updateSignatureLayout('expense', signatureLayout)
      const layouts = response.data?.data?.signature_layout
      if (layouts) setCompany(current => ({ ...(current || {}), signature_layout: layouts }))
      message.success('บันทึกตำแหน่งลายเซ็นสำหรับใบบันทึกค่าใช้จ่ายแล้ว')
    } catch (error) {
      message.error(error.response?.data?.message || 'ไม่สามารถบันทึกตำแหน่งลายเซ็นได้')
    } finally {
      setSignatureLayoutSaving(false)
    }
  }

  // VAT modes
  const vatModeOptions = [
    { value: 'no_tax', label: 'ไม่มีภาษี', color: 'green' },
    { value: 'exclude', label: 'แยกภาษี (VAT EXCLUDE)', color: 'blue' },
    { value: 'include', label: 'รวมภาษี (VAT INCLUDE)', color: 'green' }
  ];

  const calculateTotals = (items, discount = 0, taxRate = 7, vatMode = 'exclude') => {
    const totalBeforeDiscount = (items || []).reduce((sum, item) => {
      if (!item) return sum;
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + qty * price;
    }, 0);

    const discountAmount = parseFloat(discount) || 0;

    let subtotal = 0;
    let taxAmount = 0;
    let totalAmount = 0;

    if (vatMode === 'include') {
      totalAmount = totalBeforeDiscount - discountAmount;
      subtotal = totalAmount / (1 + taxRate / 100);
      taxAmount = totalAmount - subtotal;
    } else if (vatMode === 'exclude') {
      subtotal = totalBeforeDiscount - discountAmount;
      taxAmount = (subtotal * taxRate) / 100;
      totalAmount = subtotal + taxAmount;
    } else {
      subtotal = totalBeforeDiscount - discountAmount;
      taxAmount = 0;
      totalAmount = subtotal;
    }

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      discount: parseFloat(discountAmount.toFixed(2)),
      subtotalAfterDiscount: parseFloat((subtotal).toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2))
    };
  };

  // Date filter states
  const [initialMonthRange] = useState(() => {
    const today = dayjs()
    return [today.startOf('month'), today.endOf('month')]
  })
  const [selectedDateRange, setSelectedDateRange] = useState(initialMonthRange)
  const [selectedQuickFilter, setSelectedQuickFilter] = useState('this_month')
  const [selectedYear, setSelectedYear] = useState(initialMonthRange[0].year())

  const screens = useBreakpoint()
  const isMobile = !screens.md

  // Payment methods
  const paymentMethods = [
    { value: 'cash', label: 'เงินสด' },
    { value: 'transfer', label: 'โอนเงิน' },
    { value: 'credit_card', label: 'บัตรเครดิต' },
    { value: 'debit_card', label: 'บัตรเดบิต' },
    { value: 'check', label: 'เช็ค' },
    { value: 'other', label: 'อื่นๆ' }
  ]

  // Project options hook
  const { projectOptions, loading: projectsLoading } = useProjectsOptions({
    status: 'active',
    includeCode: true
  })

  // Month options
  const monthOptions = [
    { value: 0, label: 'ม.ค.' },
    { value: 1, label: 'ก.พ.' },
    { value: 2, label: 'มี.ค.' },
    { value: 3, label: 'เม.ย.' },
    { value: 4, label: 'พ.ค.' },
    { value: 5, label: 'มิ.ย.' },
    { value: 6, label: 'ก.ค.' },
    { value: 7, label: 'ส.ค.' },
    { value: 8, label: 'ก.ย.' },
    { value: 9, label: 'ต.ค.' },
    { value: 10, label: 'พ.ย.' },
    { value: 11, label: 'ธ.ค.' }
  ]
  const expensePeriodLabels = {
    all: 'ทั้งหมด',
    this_month: 'เดือนนี้',
    last_month: 'เดือนที่แล้ว',
    last_3_months: 'รอบ 3 เดือน',
    last_6_months: 'รอบ 6 เดือน',
    this_year: 'ปีนี้',
    last_year: 'ปีที่แล้ว'
  }
  const expensePeriodLabel = selectedQuickFilter === 'month_select'
    ? `${monthOptions.find(month => month.value === selectedDateRange[0]?.month())?.label || 'เดือนที่เลือก'} ${selectedYear + 543}`
    : selectedQuickFilter === 'custom'
      ? (selectedDateRange[0] || selectedDateRange[1] ? 'ช่วงวันที่เลือก' : 'ทั้งหมด')
      : (expensePeriodLabels[selectedQuickFilter] || 'ทั้งหมด')

  // Year options (current year +/- 5 years)
  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year()
    const years = []
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push({ value: i, label: (i + 543).toString() }) // Display in Buddhist year
    }
    return years
  }, [])

  useEffect(() => {
    fetchExpenses({}) // รีเฟรชครั้งแรก
    fetchSuppliers()
    fetchCategories()
    fetchSettings()
  }, [])

  // *** REMOVED: useEffect dependency เพื่อป้องกัน timing issue และ duplicate calls ***
  // แต่ละ filter handler จะจัดการ fetch เอง

  const fetchExpenses = async (customParams = {}) => {
    setLoading(true)
    try {
      // Prepare date parameters
      let startDate, endDate
      const dateRange = customParams.dateRange || selectedDateRange

      if (dateRange && dateRange[0] && dateRange[1]) {
        // Ensure they are dayjs objects before formatting
        startDate = dayjs.isDayjs(dateRange[0]) ? dateRange[0].format('YYYY-MM-DD') : dateRange[0]
        endDate = dayjs.isDayjs(dateRange[1]) ? dateRange[1].format('YYYY-MM-DD') : dateRange[1]
      }

      // Prepare filter parameters - ใช้ค่าใหม่หรือค่าเก่า
      const searchParam = customParams.search !== undefined ? customParams.search : searchTerm
      const categoryParam = customParams.category !== undefined ? customParams.category : selectedCategory
      const statusParam = customParams.status !== undefined ? customParams.status : selectedStatus
      const supplierParam = customParams.supplier !== undefined ? customParams.supplier : selectedSupplier
      const projectParam = customParams.project !== undefined ? customParams.project : selectedProject

      // สามารถเปิด debug ได้ตามต้องการ
      // console.log('FETCH EXPENSES:', { searchParam, categoryParam, statusParam, supplierParam, projectParam, startDate, endDate })

      const response = await expenseService.getAllExpenses({
        search: searchParam,
        category: categoryParam === 'all' ? '' : categoryParam,
        status: statusParam === 'all' ? '' : statusParam,
        supplier_id: supplierParam === 'all' ? '' : supplierParam,
        project_id: projectParam === 'all' ? '' : projectParam,
        start_date: startDate,
        end_date: endDate,
        limit: 50
      })
      setExpenses(response.data)
      setStats(response.summary || {})
    } catch (error) {
      //message.error('ไม่สามารถโหลดข้อมูลค่าใช้จ่ายได้')
      console.error('Error fetching expenses:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSuppliers = async () => {
    try {
      const response = await contactService.getSuppliersForDropdown()
      setSuppliers(response.data || [])
    } catch (error) {
      //console.error('Error fetching suppliers:', error)
    }
  }

  const handleOpenSupplierModal = () => {
    supplierForm.resetFields()
    setSupplierModalVisible(true)
  }

  const handleCreateSupplier = async () => {
    try {
      const values = await supplierForm.validateFields()
      setSupplierSaving(true)

      const response = await contactService.createContact({
        ...values,
        type: 'supplier'
      })
      const createdSupplierId = response?.data?.id

      await fetchSuppliers()
      if (createdSupplierId) {
        form.setFieldValue('supplier_id', createdSupplierId)
      }

      setSupplierModalVisible(false)
      supplierForm.resetFields()
      message.success('เพิ่มผู้จำหน่ายใหม่เรียบร้อยแล้ว')
    } catch (error) {
      if (error?.errorFields) return
      message.error(error.response?.data?.message || 'ไม่สามารถเพิ่มผู้จำหน่ายใหม่ได้')
    } finally {
      setSupplierSaving(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await expenseService.getCategories()
      setCategories(response.data || [])
      setDefaultCategories(response.defaultCategories || [])
    } catch (error) {
      //console.error('Error fetching categories:', error)
    }
  }



  const fetchSettings = async () => {
    try {
      const response = await getCompanySettings()
      if (response.data.success) {
        setCompany(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching settings:', error)
    }
  }

  const handleAddExpense = () => {
    setEditingExpense(null)
    setModalVisible(true)
    setFileList([])
    form.resetFields()
    form.setFieldsValue({
      expense_date: dayjs(),
      status: 'pending',
      tax_amount: 0,
      items: [{ description: '', quantity: 1, unit_price: 0 }]
    })
  }

  const handleEditExpense = async (expense) => {
    try {
      const fullExpense = await expenseService.getExpenseById(expense.id)
      setEditingExpense(fullExpense)
      setModalVisible(true)
      setFileList((fullExpense.attachments || []).map((file, index) => ({
        uid: `expense-attachment-${file.file_id || index}`,
        name: file.original_name || `เอกสาร ${index + 1}`,
        status: 'done',
        url: file.url,
        fileId: file.file_id,
        mimeType: file.mime_type,
        attachmentType: file.attachment_type || 'general',
        legacy: file.legacy
      })))

      form.setFieldsValue({
        expense_number: fullExpense.expense_number,
        expense_date: dayjs(fullExpense.expense_date),
        category: fullExpense.category,
        business_line: fullExpense.business_line || undefined,
        investor_cost_type: fullExpense.investor_cost_type || undefined,
        description: fullExpense.description,
        amount: fullExpense.amount,
        tax_amount: fullExpense.tax_amount || 0,
        payment_method: fullExpense.payment_method,
        supplier_id: fullExpense.supplier_id,
        project_id: fullExpense.project_id,
        notes: fullExpense.notes,
        status: fullExpense.status,
        vat_mode: fullExpense.vat_mode || 'exclude',
        tax_rate: fullExpense.tax_rate || 7,
        discount: fullExpense.discount || 0,
        items: fullExpense.items?.length > 0 ? fullExpense.items : [{ description: '', quantity: 1, unit_price: 0 }]
      })
    } catch (error) {
      //message.error('ไม่สามารถโหลดข้อมูลค่าใช้จ่ายได้')
    }
  }

  const handleViewExpense = async (expense) => {
    try {
      const fullExpense = await expenseService.getExpenseById(expense.id)
      setViewingExpense(fullExpense)
      setDetailVisible(true)
    } catch (error) {
      //message.error('ไม่สามารถโหลดข้อมูลค่าใช้จ่ายได้')
    }
  }

  const handleDeleteExpense = async (id) => {
    try {
      await expenseService.deleteExpense(id)
      message.success('ลบค่าใช้จ่ายสำเร็จ')
      fetchExpenses({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
    } catch (error) {
      message.error('ไม่สามารถลบค่าใช้จ่ายได้')
    }
  }

  const handleApproveExpense = async (id) => {
    try {
      await expenseService.approveExpense(id)
      message.success('อนุมัติค่าใช้จ่ายสำเร็จ')
      fetchExpenses({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
    } catch (error) {
      message.error('ไม่สามารถอนุมัติค่าใช้จ่ายได้')
    }
  }

  const handlePostToAccounting = async (expense) => {
    setPostingExpense(expense)
  }

  const handlePrint = async (expense) => {
    try {
      message.loading('กำลังสร้าง PDF...', 1)

      // โหลดข้อมูล expense แบบเต็ม
      const fullExpense = await expenseService.getExpenseById(expense.id)

      // ถ้ามี supplier_id ให้โหลดข้อมูล supplier เพิ่มเติม
      let supplierData = null
      if (fullExpense.supplier_id) {
        try {
          supplierData = await getContactById(fullExpense.supplier_id)
        } catch (err) {
          console.error('Error fetching supplier details:', err)
        }
      }

      setPdfPreviewKind('expense')
      setMobilePrintTab('preview')
      setPdfDataUrl('')
      setPdfPreviewExpense(fullExpense)
      setPdfPreviewSupplier(supplierData)
      setPdfModalVisible(true)
    } catch (error) {
      console.error('Error generating PDF:', error)
      message.error('เกิดข้อผิดพลาดในการสร้าง PDF')
    }
  }

  const handlePrintCertificate = async (expense) => {
    try {
      message.loading('กำลังสร้างใบรับรองแทนใบเสร็จ...', 1)
      const fullExpense = await expenseService.getExpenseById(expense.id)
      let supplierData = null
      if (fullExpense.supplier_id) {
        try {
          supplierData = await getContactById(fullExpense.supplier_id)
        } catch (error) {
          console.error('Error fetching supplier for expense certificate:', error)
        }
      }
      setCertificateSigners({
        claimant_name: fullExpense.certificate_claimant_name ?? fullExpense.created_by_name ?? user?.name ?? '',
        approver_name: fullExpense.certificate_approver_name ?? fullExpense.approved_by_name ?? ''
      })
      setPdfPreviewKind('certificate')
      setMobilePrintTab('preview')
      setPdfDataUrl('')
      setPdfPreviewExpense(fullExpense)
      setPdfPreviewSupplier(supplierData)
      setPdfModalVisible(true)
    } catch (error) {
      console.error('Error generating expense certificate:', error)
      message.error('ไม่สามารถสร้างใบรับรองแทนใบเสร็จได้')
    }
  }

  const handleSaveCertificateSigners = async () => {
    if (!pdfPreviewExpense?.id) return
    setCertificateSignersSaving(true)
    try {
      const names = await expenseService.updateCertificateSigners(pdfPreviewExpense.id, certificateSigners)
      setCertificateSigners(names)
      setPdfPreviewExpense(current => ({ ...current,
        certificate_claimant_name: names.claimant_name,
        certificate_approver_name: names.approver_name
      }))
      message.success('บันทึกชื่อผู้ลงนามในใบรับรองแล้ว')
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'บันทึกชื่อผู้ลงนามไม่สำเร็จ')
    } finally {
      setCertificateSignersSaving(false)
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()

      let receiptImageUrl = editingExpense?.receipt_image || null
      const existingAttachments = fileList.filter(file => file.fileId).map(file => ({
        file_id: file.fileId,
        attachment_type: file.attachmentType || 'general'
      }))
      let uploadedAttachments = []
      const filesToUpload = fileList.filter(file => file.originFileObj || (!file.url && !file.fileId))
      if (filesToUpload.length > 0) {
        setUploading(true)
        for (const attachmentType of ['payment_slip', 'product_image', 'general']) {
          const typedFiles = filesToUpload.filter(file => (file.attachmentType || 'general') === attachmentType)
          if (!typedFiles.length) continue
          const uploadResponse = await expenseService.uploadMultipleFiles(
            typedFiles.map(file => file.originFileObj || file), 'expenses'
          )
          const uploadedFiles = uploadResponse.files || []
          uploadedAttachments.push(...uploadedFiles.map(file => ({
            file_id: file.id,
            attachment_type: attachmentType,
            url: file.url
          })))
        }
        const receiptImage = uploadedAttachments.find(file => file.attachment_type === 'payment_slip')
          || uploadedAttachments.find(file => file.attachment_type === 'general')
        if (!receiptImageUrl && receiptImage) receiptImageUrl = receiptImage.url
      }

      // Calculate totals
      const items = values.items || [];
      const discount = values.discount || 0;
      const taxRate = values.tax_rate || 0;
      const vatMode = values.vat_mode || 'exclude';

      const calcValues = calculateTotals(items, discount, taxRate, vatMode);

      const expenseData = {
        ...values,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
        amount: calcValues.subtotal, // Base amount before tax
        subtotal: calcValues.subtotal,
        discount: calcValues.discount,
        tax_amount: calcValues.taxAmount,
        total_amount: calcValues.totalAmount,
        vat_mode: vatMode,
        tax_rate: taxRate,
        receipt_image: receiptImageUrl,
        attachments: [...existingAttachments, ...uploadedAttachments].map(file => ({
          file_id: file.file_id,
          attachment_type: file.attachment_type
        })),
        items: values.items?.filter(item => item.description && item.unit_price) || []
      }

      let savedExpense
      if (editingExpense) {
        savedExpense = await expenseService.updateExpense(editingExpense.id, expenseData)
        message.success('อัปเดตค่าใช้จ่ายสำเร็จ')
      } else {
        savedExpense = await expenseService.createExpense(expenseData)
        message.success('เพิ่มค่าใช้จ่ายสำเร็จ')
      }
      const driveWarnings = savedExpense?.data?.storage_organization?.warnings || []
      if (driveWarnings.length) message.warning(`บันทึก EXP สำเร็จ แต่จัดโฟลเดอร์ Google Drive ไม่ครบ: ${driveWarnings[0]}`)

      setModalVisible(false)
      setUploading(false)
      form.resetFields()
      setFileList([])
      fetchExpenses({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
    } catch (error) {
      setUploading(false)
      if (error.errors) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วน')
      } else {
        message.error(error.message || 'ไม่สามารถบันทึกข้อมูลได้')
      }
    }
  }

  const handleModalCancel = () => {
    setModalVisible(false)
    setDetailVisible(false)
    form.resetFields()
    setFileList([])
    setUploading(false)
  }

  // *** FIXED: แก้ไขปัญหา timing issue สำหรับทุก Filter ***
  const handleSearch = (value) => {
    setSearchTerm(value)
    setTimeout(() => fetchExpenses({ search: value }), 300)
  }

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category)
    setTimeout(() => fetchExpenses({ category: category }), 300)
  }

  const handleStatusFilter = (status) => {
    setSelectedStatus(status)
    setTimeout(() => fetchExpenses({ status: status }), 300)
  }

  const handleSupplierFilter = (supplier) => {
    setSelectedSupplier(supplier)
    setTimeout(() => fetchExpenses({ supplier: supplier }), 300)
  }

  const handleProjectFilter = (project) => {
    setSelectedProject(project)
    setTimeout(() => fetchExpenses({ project: project }), 300)
  }

  // Date filter handlers
  const handleQuickDateFilter = (filterType) => {
    setSelectedQuickFilter(filterType)
    const today = dayjs()
    let startDate, endDate

    // สามารถเปิด debug ได้ตามต้องการ
    // console.log('Date Filter:', filterType, 'Today:', today.format('YYYY-MM-DD'))

    switch (filterType) {
      case 'this_month':
        startDate = today.clone().startOf('month')
        endDate = today.clone().endOf('month')
        // console.log('THIS MONTH:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'))
        break
      case 'last_month':
        startDate = today.clone().subtract(1, 'month').startOf('month')
        endDate = today.clone().subtract(1, 'month').endOf('month')
        // console.log('LAST MONTH:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'))
        break
      case 'last_3_months':
        startDate = today.clone().subtract(3, 'months').startOf('month')
        endDate = today.clone().endOf('month')
        break
      case 'last_6_months':
        startDate = today.clone().subtract(6, 'months').startOf('month')
        endDate = today.clone().endOf('month')
        break
      case 'this_year':
        startDate = today.clone().startOf('year')
        endDate = today.clone().endOf('year')
        break
      case 'last_year':
        startDate = today.clone().subtract(1, 'year').startOf('year')
        endDate = today.clone().subtract(1, 'year').endOf('year')
        break
      default:
        startDate = null
        endDate = null
    }

    // console.log('Date Filter Result:', startDate?.format('YYYY-MM-DD'), 'to', endDate?.format('YYYY-MM-DD'))

    setSelectedDateRange([startDate, endDate])
    setTimeout(() => fetchExpenses({ dateRange: [startDate, endDate] }), 300)
  }

  const handleDateRangeChange = (dates) => {
    const dateRange = dates || [null, null]
    setSelectedDateRange(dateRange)
    setSelectedQuickFilter('custom')
    setTimeout(() => fetchExpenses({ dateRange: dateRange }), 300)
  }

  const handleMonthFilter = (monthValue) => {
    if (monthValue === 'all') {
      clearDateFilter()
      return
    }
    const startDate = dayjs().year(selectedYear).month(monthValue).startOf('month')
    const endDate = dayjs().year(selectedYear).month(monthValue).endOf('month')

    setSelectedQuickFilter('month_select')
    setSelectedDateRange([startDate, endDate])
    setTimeout(() => fetchExpenses({ dateRange: [startDate, endDate] }), 300)
  }

  const handleYearFilter = (yearValue) => {
    setSelectedYear(yearValue)

    // If a month is already selected, update the range for the new year
    if (selectedQuickFilter === 'month_select' && selectedDateRange[0]) {
      const currentMonth = selectedDateRange[0].month()
      const startDate = dayjs().year(yearValue).month(currentMonth).startOf('month')
      const endDate = dayjs().year(yearValue).month(currentMonth).endOf('month')
      setSelectedDateRange([startDate, endDate])
      setTimeout(() => fetchExpenses({ dateRange: [startDate, endDate] }), 300)
    } else if (selectedQuickFilter === 'this_year' || selectedQuickFilter === 'last_year' || selectedQuickFilter === 'all') {
      // If no specific month/range, just update the year state
      // (Optionally could trigger a filter for the whole year)
    }
  }

  const clearDateFilter = () => {
    const dateRange = [null, null]
    setSelectedDateRange(dateRange)
    setSelectedQuickFilter('all')
    setTimeout(() => fetchExpenses({ dateRange: dateRange }), 300)
  }

  // Keep files client-side until the expense is saved, then upload them by document role.
  const addAttachmentFiles = (attachmentType, maxCount, selectedFiles) => {
    const files = Array.from(selectedFiles || [])
    const allowedTypes = attachmentType === 'general'
      ? ['image/jpeg', 'image/png', 'application/pdf']
      : ['image/jpeg', 'image/png']
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        message.error('รองรับเฉพาะรูป JPG, PNG และ PDF สำหรับเอกสารเพิ่มเติม')
        return false
      }
      if (file.size > 10 * 1024 * 1024) {
        message.error(`ไฟล์ ${file.name} ต้องมีขนาดไม่เกิน 10 MB`)
        return false
      }
      return true
    })
    if (!validFiles.length) return
    setFileList(current => {
      const sameType = current.filter(item => (item.attachmentType || 'general') === attachmentType)
      const available = maxCount - sameType.length
      if (available < validFiles.length) {
        message.warning(attachmentType === 'payment_slip'
          ? 'แนบสลิปโอนเงินได้ 1 รูปต่อใบค่าใช้จ่าย'
          : `แนบไฟล์ประเภทนี้ได้สูงสุด ${maxCount} ไฟล์`)
      }
      if (available <= 0) return current
      return [...current, ...validFiles.slice(0, available).map(file => ({
        uid: file.uid || `${attachmentType}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        type: file.type,
        size: file.size,
        originFileObj: file,
        attachmentType,
        status: 'done'
      }))]
    })
  }

  const buildAttachmentUploadProps = (attachmentType, maxCount, accept = '.jpg,.jpeg,.png') => ({
    multiple: maxCount > 1,
    maxCount,
    fileList: fileList.filter(file => (file.attachmentType || 'general') === attachmentType),
    beforeUpload: file => {
      addAttachmentFiles(attachmentType, maxCount, [file])
      return Upload.LIST_IGNORE
    },
    onRemove: file => setFileList(current => current.filter(item => item.uid !== file.uid)),
    onPreview: file => {
      const previewUrl = file.url || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : '')
      setAttachmentPreview({ url: previewUrl, name: file.name, mime_type: file.mimeType || file.type, temporary: !file.url })
    },
    accept
  })

  const paymentSlipUploadProps = buildAttachmentUploadProps('payment_slip', 1)
  const productImageUploadProps = buildAttachmentUploadProps('product_image', 3)
  const generalUploadProps = buildAttachmentUploadProps('general', 5, '.jpg,.jpeg,.png,.pdf')

  const closeAttachmentPreview = () => {
    if (attachmentPreview?.temporary && attachmentPreview.url) URL.revokeObjectURL(attachmentPreview.url)
    setAttachmentPreview(null)
  }

  const renderMobileAttachmentPicker = (attachmentType, maxCount, label) => {
    const attachments = fileList.filter(file => (file.attachmentType || 'general') === attachmentType)
    const cameraAccept = 'image/jpeg,image/png'
    const fileAccept = attachmentType === 'general' ? `${cameraAccept},application/pdf` : cameraAccept
    const selectFiles = event => {
      addAttachmentFiles(attachmentType, maxCount, event.target.files)
      event.target.value = ''
    }
    return (
      <div className="expense-mobile-attachment-picker">
        <div className="expense-mobile-attachment-heading">
          <Text strong>{label}</Text>
          <Text type="secondary">{attachments.length}/{maxCount} ไฟล์</Text>
        </div>
        <div className="expense-mobile-attachment-buttons">
          <Button icon={<CameraOutlined />} onClick={() => cameraInputRefs.current[attachmentType]?.click()}>
            ถ่ายรูป{attachmentType === 'general' ? 'ใบเสร็จ' : ''}
          </Button>
          <Button icon={<UploadOutlined />} onClick={() => fileInputRefs.current[attachmentType]?.click()}>
            เลือกไฟล์จากเครื่อง
          </Button>
        </div>
        <input
          ref={node => { cameraInputRefs.current[attachmentType] = node }}
          className="expense-attachment-native-input"
          type="file"
          accept={cameraAccept}
          capture="environment"
          onChange={selectFiles}
        />
        <input
          ref={node => { fileInputRefs.current[attachmentType] = node }}
          className="expense-attachment-native-input"
          type="file"
          accept={fileAccept}
          multiple={maxCount > 1}
          onChange={selectFiles}
        />
        {attachments.length > 0 && (
          <div className="expense-mobile-attachment-list">
            {attachments.map(file => (
              <div key={file.uid} className="expense-mobile-attachment-file">
                <FileOutlined aria-hidden="true" />
                <span className="expense-mobile-attachment-name">{file.name}</span>
                <Button type="link" size="small" onClick={() => generalUploadProps.onPreview(file)}>ดู</Button>
                <Button type="link" danger size="small" onClick={() => generalUploadProps.onRemove(file)} aria-label={`ลบไฟล์ ${file.name}`}>
                  ลบ
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      paid: 'blue',
      rejected: 'red'
    }
    return colors[status] || 'default'
  }

  const getStatusText = (status) => {
    const texts = {
      pending: 'รอดำเนินการ',
      approved: 'อนุมัติแล้ว',
      paid: 'จ่ายแล้ว',
      rejected: 'ไม่อนุมัติ'
    }
    return texts[status] || status
  }

  const columns = [
    {
      title: 'เลขที่',
      dataIndex: 'expense_number',
      key: 'expense_number',
      width: 130,
      render: (number) => <Tag color="blue">{number}</Tag>
    },
    {
      title: 'วันที่',
      dataIndex: 'expense_date',
      key: 'expense_date',
      width: 110,
      responsive: ['xxl'],
      render: (date) => dayjs(date).format('DD/MM/YYYY')
    },
    {
      title: 'หมวดหมู่',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      responsive: ['xxl'],
      render: (category) => <Tag color="green">{category}</Tag>
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'description',
      key: 'description',
      width: 210,
      ellipsis: true
    },
    {
      title: 'ผู้จำหน่าย',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 170,
      render: (name, record) => name || record.supplier_company || '-'
    },
    {
      title: 'จำนวนเงิน',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      render: (amount) => `₿${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    },
    {
      title: 'ภาษี',
      dataIndex: 'tax_amount',
      key: 'tax_amount',
      width: 100,
      responsive: ['xxl'],
      render: (tax) => tax > 0 ? `₿${parseFloat(tax).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'ประเภทธุรกิจ',
      key: 'accounting_preset_id',
      width: 190,
      render: (_, record) => <DocumentPresetQuickSelect documentType="expense" documentId={record.id}
        value={record.accounting_preset_id} accountingStatus={record.accounting_status}
        onChanged={presetId => setExpenses(current => current.map(item => item.id === record.id ? { ...item, accounting_preset_id: presetId } : item))} />
    },
    {
      title: 'การจัดการ',
      key: 'action',
      width: 126,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4} wrap={false}>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewExpense(record)}
            size="small"
            title="ดูรายละเอียด"
          />

          <Button
            type="link"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(record)}
            size="small"
            title="พิมพ์"
            style={{ color: '#7e57c2' }}
          />

          <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: [
            { key:'certificate', label:'พิมพ์ใบรับรองแทนใบเสร็จ', icon:<FileTextOutlined />, onClick:() => handlePrintCertificate(record) },
            ['approved', 'paid'].includes(record.status) && { key:'accounting',
              label:record.accounting_status === 'posted' ? 'ลงบัญชีแล้ว' : 'ลงบัญชี', icon:<BookOutlined />,
              disabled:record.accounting_status === 'posted', onClick:() => handlePostToAccounting(record) },
            record.status === 'pending' && isSuperAdmin &&
              { key:'approve', label:'อนุมัติ', icon:<CheckOutlined />, onClick:() => handleApproveExpense(record.id) },
            isSuperAdmin && { key:'edit', label:'แก้ไข', icon:<EditOutlined />, onClick:() => handleEditExpense(record) },
            isSuperAdmin && { key:'delete', label:'ลบค่าใช้จ่าย', icon:<DeleteOutlined />, danger:true,
              onClick:() => Modal.confirm({ title:'คุณแน่ใจว่าต้องการลบค่าใช้จ่ายนี้?', okText:'ใช่', cancelText:'ไม่', okButtonProps:{ danger:true }, onOk:() => handleDeleteExpense(record.id) }) }
          ].filter(Boolean) }}>
            <Button type="link" size="small" icon={<MoreOutlined />} aria-label="การจัดการเพิ่มเติม" />
          </Dropdown>
        </Space>
      )
    }
  ]

  const handlePrintTable = async () => {
    try {
      setLoading(true)

      // Prepare filters object
      const filters = {
        search: searchTerm,
        category: selectedCategory !== 'all' ? selectedCategory : '',
        status: selectedStatus !== 'all' ? selectedStatus : '',
        supplier_id: selectedSupplier !== 'all' ? selectedSupplier : '',
        start_date: selectedDateRange[0] ? selectedDateRange[0].format('YYYY-MM-DD') : '',
        end_date: selectedDateRange[1] ? selectedDateRange[1].format('YYYY-MM-DD') : ''
      }

      const dataUrl = await generateExpensesTablePDF(expenses, company, filters, stats)
      setPdfPreviewKind('table')
      setMobilePrintTab('preview')
      setPdfPreviewExpense(null)
      setPdfPreviewSupplier(null)
      setPdfDataUrl(dataUrl)
      setPdfModalVisible(true)
    } catch (error) {
      console.error('Error printing table:', error)
      message.error('ไม่สามารถพิมพ์ตารางได้')
    } finally {
      setLoading(false)
    }
  }

  // Mobile Expense Card Component
  const ExpenseCard = ({ expense }) => (
    <Card
      className="mobile-expense-card"
    >
      <Row gutter={12}>
        <Col span={24}>
          <div className="mobile-expense-tags">
            <Tag color="blue" style={{ marginBottom: 4 }}>{expense.expense_number}</Tag>
            <Tag color={getStatusColor(expense.status)}>{getStatusText(expense.status)}</Tag>
          </div>

          <Text strong className="mobile-expense-description">
            {expense.description}
          </Text>

          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary">วันที่:</Text><br />
              <Text>{dayjs(expense.expense_date).format('DD/MM/YYYY')}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">หมวดหมู่:</Text><br />
              <Tag color="green" size="small">{expense.category}</Tag>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary">จำนวนเงิน:</Text><br />
              <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                ₿{parseFloat(expense.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">ภาษี:</Text><br />
              <Text style={{ fontSize: 16 }}>
                {expense.tax_amount > 0 ? `₿${parseFloat(expense.tax_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
              </Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={24}>
              <Text type="secondary">ผู้จำหน่าย:</Text><br />
              <Text>{expense.supplier_name || expense.supplier_company || '-'}</Text>
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="mobile-expense-card-actions">
        <Button icon={<EyeOutlined />} onClick={() => handleViewExpense(expense)}>ดูรายละเอียด</Button>
        {isSuperAdmin && (
          <Button type="primary" icon={<EditOutlined />} onClick={() => handleEditExpense(expense)}>แก้ไข</Button>
        )}
        <Dropdown trigger={['click']} placement="topRight" menu={{ items: [
          { key: 'print', label: 'พิมพ์ใบบันทึกค่าใช้จ่าย', icon: <PrinterOutlined />, onClick: () => handlePrint(expense) },
          { key: 'certificate', label: 'พิมพ์ใบรับรองแทนใบเสร็จ', icon: <FileTextOutlined />, onClick: () => handlePrintCertificate(expense) },
          ['approved', 'paid'].includes(expense.status) && {
            key: 'accounting', label: expense.accounting_status === 'posted' ? 'ลงบัญชีแล้ว' : 'ลงบัญชี',
            icon: <BookOutlined />, disabled: expense.accounting_status === 'posted', onClick: () => handlePostToAccounting(expense)
          },
          expense.status === 'pending' && isSuperAdmin && {
            key: 'approve', label: 'อนุมัติ', icon: <CheckOutlined />, onClick: () => handleApproveExpense(expense.id)
          },
          isSuperAdmin && {
            key: 'delete', label: 'ลบค่าใช้จ่าย', icon: <DeleteOutlined />, danger: true,
            onClick: () => Modal.confirm({
              title: 'ลบค่าใช้จ่ายนี้?', okText: 'ลบ', cancelText: 'ยกเลิก', okButtonProps: { danger: true },
              onOk: () => handleDeleteExpense(expense.id)
            })
          }
        ].filter(Boolean) }}>
          <Button icon={<MoreOutlined />}>เพิ่มเติม</Button>
        </Dropdown>
      </div>
    </Card>
  )

  // Render Filters Component
  const FiltersComponent = () => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Search
          placeholder="ค้นหาค่าใช้จ่าย..."
          allowClear
          onSearch={handleSearch}
          style={{ width: '100%' }}
        />
      </Col>

      {/* Date Filter Section */}
      <Col span={24}>
        <Divider orientation="left" style={{ margin: '16px 0 8px 0' }}>
          <CalendarOutlined /> กรองตามวันที่
        </Divider>

        {/* Quick Date Filters */}
        <Space wrap style={{ marginBottom: 12 }}>
          <Button
            size="small"
            type={selectedQuickFilter === 'all' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('all')}
          >
            ทั้งหมด
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'this_month' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('this_month')}
          >
            เดือนนี้
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'last_month' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('last_month')}
          >
            เดือนที่แล้ว
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'last_3_months' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('last_3_months')}
          >
            รอบ 3 เดือน
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'last_6_months' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('last_6_months')}
          >
            รอบ 6 เดือน
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'this_year' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('this_year')}
          >
            ปีนี้
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'last_year' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('last_year')}
          >
            ปีที่แล้ว
          </Button>
        </Space>

        {/* Custom Date Range Picker */}
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col span={24}>
            <RangePicker
              value={selectedDateRange}
              onChange={handleDateRangeChange}
              style={{ width: '100%', marginBottom: 8 }}
              placeholder={['วันเริ่มต้น', 'วันสิ้นสุด']}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col span={12}>
            <Select
              placeholder="เลือกปี"
              style={{ width: '100%' }}
              onChange={handleYearFilter}
              value={selectedYear}
            >
              {yearOptions.map(y => (
                <Option key={y.value} value={y.value}>{y.label}</Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            <Select
              placeholder="เลือกเดือน"
              style={{ width: '100%' }}
              onChange={handleMonthFilter}
              value={selectedQuickFilter === 'month_select' ? selectedDateRange[0]?.month() : undefined}
              allowClear
              onClear={() => clearDateFilter()}
            >
              {monthOptions.map(m => (
                <Option key={m.value} value={m.value}>{m.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        {(selectedDateRange[0] || selectedDateRange[1]) && (
          <Button
            size="small"
            type="link"
            onClick={clearDateFilter}
            style={{ padding: 0 }}
          >
            ล้างตัวกรองวันที่
          </Button>
        )}
      </Col>

      <Col span={24}>
        <Select
          value={selectedCategory}
          onChange={handleCategoryFilter}
          style={{ width: '100%' }}
          placeholder="เลือกหมวดหมู่"
        >
          <Option value="all">ทุกหมวดหมู่</Option>
          {defaultCategories.map(category => (
            <Option key={category} value={category}>
              {category}
            </Option>
          ))}
        </Select>
      </Col>

      <Col span={24}>
        <Select
          value={selectedStatus}
          onChange={handleStatusFilter}
          style={{ width: '100%' }}
          placeholder="เลือกสถานะ"
        >
          <Option value="all">ทุกสถานะ</Option>
          <Option value="pending">รอดำเนินการ</Option>
          <Option value="approved">อนุมัติแล้ว</Option>
          <Option value="paid">จ่ายแล้ว</Option>
          <Option value="rejected">ไม่อนุมัติ</Option>
        </Select>
      </Col>

      <Col span={24}>
        <Select
          value={selectedSupplier}
          onChange={handleSupplierFilter}
          style={{ width: '100%' }}
          placeholder="เลือกผู้จำหน่าย"
          showSearch
          optionFilterProp="children"
        >
          <Option value="all">ทุกผู้จำหน่าย</Option>
          {suppliers.map(supplier => (
            <Option key={supplier.id} value={supplier.id}>
              {supplier.name} {supplier.company_name && `(${supplier.company_name})`}
            </Option>
          ))}
        </Select>
      </Col>

      {/* ✅ ปรับให้ ProjectFilter กว้างขึ้น */}
      <Col span={24}>
        <Select
          value={selectedProject}
          onChange={handleProjectFilter}
          style={{ width: '100%', maxWidth: 800 }}
          placeholder="เลือกโครงการ"
          showSearch
          optionFilterProp="children"
          loading={projectsLoading}
        >
          <Option value="all">ทุกโครงการ</Option>
          {projectOptions.map(project => (
            <Option key={project.value} value={project.value}>
              {project.label}
            </Option>
          ))}
        </Select>
      </Col>
    </Row>
  )

  return (
    <div className={`expenses-page ${isMobile?'mobile-layout':''}`} style={{ padding: isMobile ? 0 : 24 }}>
      <Title level={2} className="expenses-page-title">
        <MoneyCollectOutlined /> จัดการค่าใช้จ่าย
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[12,12]} className="expense-stats-grid" style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card className="expense-stat-card total">
            <Statistic
              title={`ค่าใช้จ่ายรวม (${expensePeriodLabel})`}
              value={stats.total_amount || 0}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="₿"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="expense-stat-card approved">
            <Statistic
              title="อนุมัติแล้ว"
              value={stats.approved_amount || 0}
              precision={2}
              prefix={<CheckOutlined />}
              suffix="₿"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="expense-stat-card pending">
            <Statistic
              title="รอดำเนินการ"
              value={stats.pending_amount || 0}
              precision={2}
              prefix={<ExclamationCircleOutlined />}
              suffix="₿"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="expense-stat-card count">
            <Statistic
              title="จำนวนรายการ"
              value={stats.total_expenses || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" className="expense-reconciliation-card" style={{ marginBottom: 16 }} title="กระทบยอดเอกสารค่าใช้จ่ายตามตัวกรอง (ข้อมูลทุกหน้า)">
        <Row gutter={[16, 12]}>
          <Col xs={12} md={6}><Statistic title="ค่าใช้จ่ายก่อน VAT" value={stats.expense_before_vat || 0} precision={2} suffix="บาท" /></Col>
          <Col xs={12} md={6}><Statistic title="ภาษีซื้อ" value={stats.input_vat || 0} precision={2} suffix="บาท" /></Col>
          <Col xs={12} md={6}><Statistic title="ยอดเอกสารที่ลงบัญชีแล้ว" value={stats.posted_document_total || 0} precision={2} suffix="บาท" valueStyle={{ color: '#3f8600' }} /></Col>
          <Col xs={12} md={6}><Statistic title="ยอดเอกสารที่ยังไม่ลงบัญชี" value={stats.unposted_document_total || 0} precision={2} suffix="บาท" valueStyle={{ color: '#fa8c16' }} /></Col>
        </Row>
      </Card>

      {/* Desktop View */}
      {!isMobile && (
        <>
          {/* Filters and Controls */}
          <Card style={{ marginBottom: 16 }}>
            {/* Date Filter Row */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Space wrap style={{ width: '100%' }}>
                  <Text strong>
                    <CalendarOutlined /> กรองตามช่วงเวลา:
                  </Text>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'all' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('all')}
                  >
                    ทั้งหมด
                  </Button>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'this_month' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('this_month')}
                  >
                    เดือนนี้
                  </Button>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'last_month' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('last_month')}
                  >
                    เดือนที่แล้ว
                  </Button>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'last_3_months' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('last_3_months')}
                  >
                    รอบ 3 เดือน
                  </Button>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'last_6_months' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('last_6_months')}
                  >
                    รอบ 6 เดือน
                  </Button>
                  <RangePicker
                    value={selectedDateRange}
                    onChange={handleDateRangeChange}
                    placeholder={['วันเริ่มต้น', 'วันสิ้นสุด']}
                    format="DD/MM/YYYY"
                    style={{ width: 240 }}
                  />
                  <Select
                    placeholder="ปี"
                    style={{ width: 90 }}
                    onChange={handleYearFilter}
                    value={selectedYear}
                  >
                    {yearOptions.map(y => (
                      <Option key={y.value} value={y.value}>{y.label}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="เดือน"
                    style={{ width: 90 }}
                    onChange={handleMonthFilter}
                    value={selectedQuickFilter === 'month_select' ? selectedDateRange[0]?.month() : undefined}
                    allowClear
                    onClear={() => clearDateFilter()}
                  >
                    {monthOptions.map(m => (
                      <Option key={m.value} value={m.value}>{m.label}</Option>
                    ))}
                  </Select>
                  {(selectedDateRange[0] || selectedDateRange[1]) && (
                    <Button
                      size="small"
                      type="link"
                      onClick={clearDateFilter}
                      danger
                    >
                      ล้างตัวกรอง
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>

            {/* Other Filters Row */}
            <Row gutter={16} align="middle">
              <Col xs={24} sm={5}>
                <Search
                  placeholder="ค้นหาค่าใช้จ่าย..."
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={3}>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryFilter}
                  style={{ width: '100%' }}
                  placeholder="หมวดหมู่"
                >
                  <Option value="all">ทุกหมวดหมู่</Option>
                  {defaultCategories.map(category => (
                    <Option key={category} value={category}>
                      {category}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={3}>
                <Select
                  value={selectedStatus}
                  onChange={handleStatusFilter}
                  style={{ width: '100%' }}
                  placeholder="สถานะ"
                >
                  <Option value="all">ทุกสถานะ</Option>
                  <Option value="pending">รอดำเนินการ</Option>
                  <Option value="approved">อนุมัติแล้ว</Option>
                  <Option value="paid">จ่ายแล้ว</Option>
                  <Option value="rejected">ไม่อนุมัติ</Option>
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  value={selectedSupplier}
                  onChange={handleSupplierFilter}
                  style={{ width: '100%' }}
                  placeholder="ผู้จำหน่าย"
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value="all">ทุกผู้จำหน่าย</Option>
                  {suppliers.map(supplier => (
                    <Option key={supplier.id} value={supplier.id}>
                      {supplier.name} {supplier.company_name && `(${supplier.company_name})`}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* ✅ ปรับให้ ProjectFilter กว้างขึ้น */}
              <Col span={4}>
                <Select
                  value={selectedProject}
                  onChange={handleProjectFilter}
                  style={{ width: '100%' }}
                  placeholder="เลือกโครงการ"
                  showSearch
                  optionFilterProp="children"
                  loading={projectsLoading}
                >
                  <Option value="all">ทุกโครงการ</Option>
                  {projectOptions.map(project => (
                    <Option key={project.value} value={project.value}>
                      {project.label}
                    </Option>
                  ))}
                </Select>
              </Col>

              {isSuperAdmin && (
                <>
                  <Col xs={3} sm={3} style={{ textAlign: 'right', paddingLeft: 0 }}>
                    <Space>
                      <Button
                        icon={<PrinterOutlined />}
                        onClick={handlePrintTable}
                      >
                        พิมพ์
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddExpense}
                      >
                        เพิ่มค่าใช้จ่าย
                      </Button>
                    </Space>
                  </Col>
                </>
              )}

            </Row>
          </Card>

          {/* Expenses Table */}
          <Card style={{ minWidth: 0 }}>
            <Table
              columns={columns}
              tableLayout="fixed"
              dataSource={expenses}
              rowKey="id"
              loading={loading}
              scroll={{ x: 'max-content' }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} จาก ${total} รายการ`
              }}
              footer={() => {
                const totalAmount = expenses.reduce((sum, expense) => sum + parseFloat(expense.total_amount || 0), 0)
                const totalTax = expenses.reduce((sum, expense) => sum + parseFloat(expense.tax_amount || 0), 0)

                return (
                  <div style={{
                    padding: '8px 16px',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 18
                  }}>
                    <div>
                      <Text strong>ยอดรวมทั้งสิ้น:</Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div><Text strong>จำนวนเงิน:</Text> {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      <div><Text strong>ภาษีซื้อ:</Text> {totalTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                )
              }}
            />
          </Card>
        </>
      )}

      {/* Mobile View */}
      {isMobile && (
        <>
          {/* Mobile Filter Bar */}
          {/* Mobile Filter Bar */}
          <Card className="mobile-expense-filter-bar" bodyStyle={{ padding: '12px' }}>
            <Row gutter={8} align="middle" wrap={false}>
              <Col flex="auto">
                <Search
                  placeholder="ค้นหา..."
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col flex="none">
                <Badge count={[selectedCategory!=='all',selectedStatus!=='all',selectedSupplier!=='all',selectedProject!=='all'].filter(Boolean).length} size="small">
                  <Button icon={<FilterOutlined />} onClick={() => setFiltersVisible(true)} />
                </Badge>
              </Col>
            </Row>
            <div className="mobile-expense-quick-filters">
              {[['all','ทั้งหมด'],['this_month','เดือนนี้'],['last_month','เดือนที่แล้ว'],['last_3_months','3 เดือน'],['this_year','ปีนี้']].map(([key,label])=><Button key={key} size="small" type={selectedQuickFilter===key?'primary':'default'} onClick={()=>handleQuickDateFilter(key)}>{label}</Button>)}
            </div>
          </Card>

          {/* Mobile Expense List */}
          <List className="mobile-expense-list"
            loading={loading}
            dataSource={expenses}
            renderItem={(expense) => (
              <List.Item style={{ padding: 0 }}>
                <ExpenseCard expense={expense} />
              </List.Item>
            )}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              simple: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]}/${total}`
            }}
          />

          {/* Mobile Filters Drawer */}
          <Drawer
            className="mobile-expense-filters-drawer"
            title="ตัวกรอง"
            placement="bottom"
            onClose={() => setFiltersVisible(false)}
            open={filtersVisible}
            height="auto"
          >
            <FiltersComponent />
          </Drawer>

          {/* Float Button */}
          <FloatButton
            icon={<PlusOutlined />}
            type="primary"
            style={{ right: 18, bottom: 'calc(82px + env(safe-area-inset-bottom))' }}
            onClick={handleAddExpense}
          />
        </>
      )}

      {/* Add/Edit Expense Modal/Drawer */}
      {isMobile ? (
        <Drawer
          className="mobile-expense-form-drawer"
          title={editingExpense ? 'แก้ไขค่าใช้จ่าย' : 'เพิ่มค่าใช้จ่ายใหม่'}
          placement="right"
          onClose={handleModalCancel}
          open={modalVisible}
          width="100%"
          destroyOnClose={true}
          extra={
            <Space>
              <Button onClick={handleModalCancel}>ยกเลิก</Button>
              <Button
                type="primary"
                onClick={handleModalOk}
                loading={uploading}
              >
                บันทึก
              </Button>
            </Space>
          }
        >
          <ExpenseForm />
        </Drawer>
      ) : (
        <Modal
          title={editingExpense ? 'แก้ไขค่าใช้จ่าย' : 'เพิ่มค่าใช้จ่ายใหม่'}
          open={modalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={800}
          okText="บันทึก"
          cancelText="ยกเลิก"
          confirmLoading={uploading}
          destroyOnClose={true}
        >
          <ExpenseForm />
        </Modal>
      )}

      <Modal
        title="เพิ่มผู้จำหน่ายใหม่"
        open={supplierModalVisible}
        onOk={handleCreateSupplier}
        onCancel={() => {
          setSupplierModalVisible(false)
          supplierForm.resetFields()
        }}
        okText="บันทึกผู้จำหน่าย"
        cancelText="ยกเลิก"
        confirmLoading={supplierSaving}
        width={isMobile ? 'calc(100% - 24px)' : 620}
        zIndex={1300}
        bodyStyle={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}
        destroyOnClose
      >
        <Form form={supplierForm} layout="vertical" preserve={false}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="ชื่อผู้จำหน่าย"
                rules={[{ required: true, whitespace: true, message: 'กรุณาระบุชื่อผู้จำหน่าย' }]}
              >
                <Input placeholder="ชื่อร้าน หรือชื่อผู้ติดต่อ" autoFocus />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="company_name" label="ชื่อบริษัท/ร้านค้า">
                <Input placeholder="ชื่อที่จดทะเบียน (ถ้ามี)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="tax_id" label="เลขประจำตัวผู้เสียภาษี">
                <Input placeholder="เลขประจำตัวผู้เสียภาษี 13 หลัก" maxLength={20} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="phone" label="เบอร์โทรศัพท์">
                <Input placeholder="เบอร์โทรศัพท์" maxLength={30} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="อีเมล"
            rules={[{ type: 'email', message: 'รูปแบบอีเมลไม่ถูกต้อง' }]}
          >
            <Input placeholder="example@company.com" />
          </Form.Item>

          <Form.Item name="address" label="ที่อยู่">
            <Input.TextArea rows={3} placeholder="ที่อยู่สำหรับออกเอกสาร" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Expense Detail Modal/Drawer */}
      {isMobile ? (
        <Drawer
          className="mobile-expense-detail-drawer"
          title="รายละเอียดค่าใช้จ่าย"
          placement="right"
          onClose={handleModalCancel}
          open={detailVisible}
          width="100%"
        >
          <ExpenseDetail />
        </Drawer>
      ) : (
        <Modal
          title="รายละเอียดค่าใช้จ่าย"
          open={detailVisible}
          onCancel={handleModalCancel}
          width={700}
          footer={[
            <Button key="close" onClick={handleModalCancel}>
              ปิด
            </Button>
          ]}
        >
          <ExpenseDetail />
        </Modal>
      )}

      {/* PDF Preview Modal */}
      <AccountingPostingPreviewModal open={Boolean(postingExpense)} documentType="expense" documentId={postingExpense?.id}
        onClose={() => setPostingExpense(null)} onPosted={() => fetchExpenses({})} />

      <Modal
        className="expense-print-modal"
        title={pdfPreviewKind === 'table'
          ? 'ตัวอย่างรายงานรายการค่าใช้จ่าย'
          : pdfPreviewKind === 'certificate'
            ? 'ตัวอย่างใบรับรองแทนใบเสร็จรับเงิน'
            : 'ตัวอย่างใบบันทึกค่าใช้จ่าย'}
        open={pdfModalVisible}
        onCancel={() => {
          setPdfModalVisible(false)
          setPdfPreviewExpense(null)
          setPdfPreviewSupplier(null)
          setPdfDataUrl('')
          setPdfLoading(false)
        }}
        footer={[
          <Button key="download" type="primary" disabled={!pdfDataUrl || pdfLoading || certificateSignersDirty} onClick={() => {
            const link = document.createElement('a')
            link.href = pdfObjectUrl || pdfDataUrl
            link.download = pdfPreviewKind === 'certificate'
              ? `expense-certificate-${pdfPreviewExpense?.expense_number || dayjs().format('YYYYMMDD-HHmm')}.pdf`
              : `expenses-report-${dayjs().format('YYYYMMDD-HHmm')}.pdf`
            link.click()
          }}>ดาวน์โหลด PDF</Button>
        ]}
        width={isMobile ? '100vw' : 'min(1100px, calc(100vw - 32px))'}
        centered
      >
        <div className="expense-print-workspace">
          {isMobile && pdfPreviewKind !== 'table' && <div className="expense-print-mobile-tabs" role="tablist" aria-label="ส่วนของหน้าต่างพิมพ์เอกสาร">
            <button type="button" role="tab" aria-selected={mobilePrintTab === 'settings'}
              className={mobilePrintTab === 'settings' ? 'is-active' : ''}
              onClick={() => setMobilePrintTab('settings')}>ตั้งค่าเอกสาร</button>
            <button type="button" role="tab" aria-selected={mobilePrintTab === 'preview'}
              className={mobilePrintTab === 'preview' ? 'is-active' : ''}
              onClick={() => setMobilePrintTab('preview')}>ตัวอย่าง PDF</button>
          </div>}
          {['expense', 'certificate'].includes(pdfPreviewKind) && (
            <div className={`expense-print-settings${isMobile && mobilePrintTab !== 'settings' ? ' expense-print-mobile-hidden' : ''}`}>
              {pdfPreviewKind === 'expense' && <div style={{ padding: 12, marginBottom: 10, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafcff' }}>
                <Row gutter={[12, 8]} align="bottom">
                  <Col xs={24} sm={10}>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>ชื่อผู้บันทึกในเอกสาร</Text>
                    <Select
                      value={expensePrintOptions.recorder_mode}
                      onChange={value => updateExpensePrintOptions({ recorder_mode: value })}
                      style={{ width: '100%' }}
                      options={[
                        { value: 'current_user', label: 'ใช้ User ปัจจุบัน' },
                        { value: 'custom', label: 'กำหนดชื่อเอง' }
                      ]}
                    />
                  </Col>
                  <Col xs={24} sm={14}>
                    {expensePrintOptions.recorder_mode === 'custom' ? (
                      <Input
                        value={expensePrintOptions.recorder_custom_name}
                        onChange={event => updateExpensePrintOptions({ recorder_custom_name: event.target.value })}
                        placeholder="กรอกชื่อผู้บันทึก"
                      />
                    ) : (
                      <div style={{ minHeight: 32, display: 'flex', alignItems: 'center' }}>
                        <Text type="secondary">ใช้ชื่อ: {user?.name || user?.email || 'ไม่พบชื่อผู้ใช้งาน'}</Text>
                      </div>
                    )}
                  </Col>
                </Row>
              </div>}
              {pdfPreviewKind === 'certificate' && <div style={{ padding: 12, marginBottom: 10, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafcff' }}>
                <Row gutter={[12, 10]} align="bottom">
                  <Col xs={24} sm={10}>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>ชื่อผู้เบิกจ่าย</Text>
                    <Input maxLength={150} disabled={certificateSignersSaving} value={certificateSigners.claimant_name}
                      onChange={event => setCertificateSigners(current => ({ ...current, claimant_name:event.target.value }))}
                      placeholder="กรอกชื่อผู้เบิกจ่าย" />
                  </Col>
                  <Col xs={24} sm={10}>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>ชื่อผู้อนุมัติ</Text>
                    <Input maxLength={150} disabled={certificateSignersSaving} value={certificateSigners.approver_name}
                      onChange={event => setCertificateSigners(current => ({ ...current, approver_name:event.target.value }))}
                      placeholder="กรอกชื่อผู้อนุมัติ" />
                  </Col>
                  <Col xs={24} sm={4}>
                    <Button type="primary" block loading={certificateSignersSaving} onClick={handleSaveCertificateSigners}>บันทึกชื่อ</Button>
                  </Col>
                </Row>
                <Text type="secondary" style={{ display:'block', marginTop:8 }}>
                  {certificateSignersDirty ? 'บันทึกชื่อก่อนดาวน์โหลด PDF' : 'ชื่อที่บันทึกจะใช้กับใบค่าใช้จ่ายนี้เมื่อเปิดพิมพ์ครั้งถัดไป'}
                </Text>
              </div>}
              {pdfPreviewKind === 'expense' && (
                <SignatureLayoutControls
                  documentType="expense"
                  value={signatureLayout}
                  onChange={setSignatureLayout}
                  onSave={handleSaveSignatureLayout}
                  saving={signatureLayoutSaving}
                />
              )}
              {isMobile && <Button type="primary" block onClick={() => setMobilePrintTab('preview')}>ดูตัวอย่าง PDF</Button>}
            </div>
          )}
          <div className={`expense-print-preview${isMobile && pdfPreviewKind !== 'table' && mobilePrintTab !== 'preview' ? ' expense-print-mobile-hidden' : ''}`}>
            {isMobile && pdfDataUrl && <Button size="small" className="expense-print-open-pdf"
              disabled={certificateSignersDirty} onClick={handleOpenExpensePdf}>เปิดไฟล์ PDF ในแอปอื่น</Button>}
            <div className="expense-print-preview-canvas">
            {pdfLoading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', zIndex: 1 }}><Spin size="large" /></div>}
            {pdfDataUrl && (isMobile
              ? (pdfPreviewKind === 'table' || mobilePrintTab === 'preview'
                  ? <MobilePdfPreview dataUrl={pdfDataUrl} openPdf={handleOpenExpensePdf} />
                  : null)
              : <iframe src={pdfObjectUrl || pdfDataUrl} className="expense-print-preview-frame" title="PDF Preview" />)}
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={attachmentPreview?.original_name || attachmentPreview?.name || 'ตัวอย่างเอกสาร'}
        open={Boolean(attachmentPreview)} footer={null} width="min(960px, 95vw)"
        destroyOnClose onCancel={closeAttachmentPreview}
      >
        {attachmentPreview?.temporary && attachmentPreview.mime_type?.startsWith('image/')
          ? <Image src={attachmentPreview.url} width="100%" preview={false} alt={attachmentPreview.name} />
          : attachmentPreview?.temporary && attachmentPreview.mime_type === 'application/pdf'
            ? <Button type="primary" onClick={() => window.open(attachmentPreview.url, '_blank')}>เปิด PDF</Button>
            : attachmentPreview && <FileViewer
              fileUrl={getFileUrl(attachmentPreview.url)} imageWidth="100%" pdfHeight={650}
            />}
      </Modal>
    </div>
  );

  // Expense Form Component
  function ExpenseForm() {
    const [calcValues, setCalcValues] = useState({
      subtotal: 0,
      discount: 0,
      subtotalAfterDiscount: 0,
      taxAmount: 0,
      totalAmount: 0
    });

    const handleFormChange = async (changedValues) => {
      const items = form.getFieldValue('items') || [];
      const discount = form.getFieldValue('discount') || 0;
      const taxRate = form.getFieldValue('tax_rate') || 7;
      const currentVatMode = form.getFieldValue('vat_mode') || 'exclude';

      const calculations = calculateTotals(items, discount, taxRate, currentVatMode);
      setCalcValues(calculations);

      // Auto-generate expense number when date changes
      if (changedValues && changedValues.expense_date) {
        try {
          const formattedDate = changedValues.expense_date.format('YYYY-MM-DD');
          const newNumber = await expenseService.generateExpenseNumber(formattedDate);
          form.setFieldsValue({ expense_number: newNumber });
        } catch (error) {
          console.error('Error generating expense number:', error);
        }
      }
    };

    // Initial calculation
    useEffect(() => {
      handleFormChange();
    }, []);

    return (
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        initialValues={{
          expense_date: dayjs(),
          status: 'pending',
          tax_amount: 0,
          vat_mode: 'exclude',
          tax_rate: 7,
          discount: 0,
          items: [{ description: '', quantity: 1, unit_price: 0 }]
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="expense_number"
              label="เลขที่เอกสาร"
              rules={[{ required: true, message: 'กรุณาใส่เลขที่เอกสาร' }]}
            >
              <Input placeholder="เช่น EXP6810001" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="expense_date"
              label="วันที่"
              rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="category"
              label="หมวดหมู่"
              rules={[{ required: true, message: 'กรุณาเลือกหมวดหมู่' }]}
            >
              <Select placeholder="เลือกหมวดหมู่">
                {defaultCategories.map(category => (
                  <Option key={category} value={category}>
                    {category}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}><Form.Item name="business_line" label="ประเภทธุรกิจสำหรับส่วนแบ่งนักลงทุน" rules={[{required:true,message:'กรุณาเลือกประเภทธุรกิจ'}]}>
            <Select options={[{value:'software',label:'ธุรกิจซอฟต์แวร์'},{value:'sign_printing',label:'ธุรกิจร้านป้าย'},{value:'shared',label:'ค่าใช้จ่ายร่วม'}]}/>
          </Form.Item></Col>
          <Col xs={24} sm={12}><Form.Item name="investor_cost_type" label="การนับค่าใช้จ่ายในส่วนแบ่ง" rules={[{required:true,message:'กรุณาเลือกประเภทต้นทุน'}]}>
            <Select options={[{value:'operating',label:'ค่าใช้จ่ายดำเนินงาน — หักส่วนแบ่ง'},{value:'bill_cost',label:'อยู่ในต้นทุนบิลแล้ว — ไม่หักซ้ำ'},{value:'excluded',label:'ไม่เกี่ยวกับฐานส่วนแบ่ง'}]}/>
          </Form.Item></Col>
        </Row>

        <Form.Item
          name="description"
          label="รายละเอียด"
          rules={[{ required: true, message: 'กรุณาใส่รายละเอียด' }]}
        >
          <Input.TextArea rows={3} placeholder="รายละเอียดค่าใช้จ่าย..." />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="supplier_id"
              label={(
                <Space size={6}>
                  <span>ผู้จำหน่าย</span>
                  {canCreateSupplier && (
                    <Button
                      type="link"
                      size="small"
                      htmlType="button"
                      icon={<PlusOutlined />}
                      onClick={handleOpenSupplierModal}
                      style={{ height: 'auto', padding: 0 }}
                    >
                      เพิ่มผู้จำหน่าย
                    </Button>
                  )}
                </Space>
              )}
            >
              <Select
                placeholder="เลือกผู้จำหน่าย"
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {suppliers.map(supplier => (
                  <Option key={supplier.id} value={supplier.id}>
                    {supplier.name} {supplier.company_name && `(${supplier.company_name})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="payment_method"
              label="วิธีการชำระเงิน"
            >
              <Select placeholder="เลือกวิธีการชำระเงิน">
                {paymentMethods.map(method => (
                  <Option key={method.value} value={method.value}>
                    {method.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="project_id"
              label="โครงการ"
            >
              <Select
                placeholder="เลือกโครงการ"
                allowClear
                showSearch
                optionFilterProp="children"
                loading={projectsLoading}
              >
                {projectOptions.map(project => (
                  <Option key={project.value} value={project.value}>
                    {project.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider>รายการค่าใช้จ่าย</Divider>

        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={8} align="bottom">
                  <Col xs={24} sm={10}>
                    <Form.Item
                      {...restField}
                      name={[name, 'description']}
                      label={key === 0 ? 'รายละเอียด' : ''}
                      rules={[{ required: true, message: 'กรุณาใส่รายละเอียด' }]}
                    >
                      <Input placeholder="รายละเอียดรายการ" />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={4}>
                    <Form.Item
                      {...restField}
                      name={[name, 'quantity']}
                      label={key === 0 ? 'จำนวน' : ''}
                      rules={[{ required: true, message: 'จำนวน' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0.01}
                        placeholder="1"
                        precision={2}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'unit_price']}
                      label={key === 0 ? 'ราคาต่อหน่วย' : ''}
                      rules={[{ required: true, message: 'ราคา' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        placeholder="0.00"
                        precision={2}
                        addonAfter="₿"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={4}>
                    <Form.Item label={key === 0 ? 'ลบ' : ''}>
                      {fields.length > 1 && (
                        <Button
                          type="dashed"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
              >
                เพิ่มรายการ
              </Button>
            </>
          )}
        </Form.List>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="vat_mode"
              label="รูปแบบภาษี"
            >
              <Select>
                {vatModeOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="tax_rate"
              label="อัตราภาษี (%)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="discount"
              label="ส่วนลด"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\฿\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Summary Section */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <div style={{ width: '300px' }}>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col>รวมเป็นเงิน:</Col>
              <Col>฿{calcValues.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 8, color: 'red' }}>
              <Col>ส่วนลด:</Col>
              <Col>-฿{calcValues.discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col>ภาษีมูลค่าเพิ่ม:</Col>
              <Col>฿{calcValues.taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
            <Row justify="space-between" style={{ fontWeight: 'bold', fontSize: '16px' }}>
              <Col>จำนวนเงินรวมทั้งสิ้น:</Col>
              <Col style={{ color: '#1890ff' }}>฿{calcValues.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Col>
            </Row>
          </div>
        </div>


        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="status"
              label="สถานะ"
            >
              <Select>
                <Option value="pending">รอดำเนินการ</Option>
                <Option value="approved">อนุมัติแล้ว</Option>
                <Option value="paid">จ่ายแล้ว</Option>
                <Option value="rejected">ไม่อนุมัติ</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label="หมายเหตุ"
        >
          <Input.TextArea rows={2} placeholder="หมายเหตุเพิ่มเติม..." />
        </Form.Item>

        <Divider orientation="left">เอกสารแนบ</Divider>
        {isMobile ? (
          <div className="expense-mobile-attachments">
            <Text type="secondary">ถ่ายรูปด้วยกล้องหรือเลือกไฟล์จากเครื่อง แล้วกดบันทึกค่าใช้จ่ายเพื่ออัปโหลด</Text>
            {renderMobileAttachmentPicker('general', 5, 'ใบเสร็จและเอกสารอื่น (JPG, PNG, PDF)')}
            {renderMobileAttachmentPicker('payment_slip', 1, 'สลิปโอนเงิน (JPG, PNG)')}
            {renderMobileAttachmentPicker('product_image', 3, 'รูปสินค้า/บริการ (JPG, PNG)')}
          </div>
        ) : (
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <Card size="small" title="สลิปโอนเงิน" extra={<Tag color="blue">สูงสุด 1 รูป</Tag>}>
              <Dragger {...paymentSlipUploadProps}>
                <p className="ant-upload-drag-icon"><MoneyCollectOutlined /></p>
                <p className="ant-upload-text">แนบรูปสลิปการชำระเงิน</p>
                <p className="ant-upload-hint">รองรับ JPG และ PNG</p>
              </Dragger>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" title="รูปสินค้า/บริการ" extra={<Tag color="green">สูงสุด 3 รูป</Tag>}>
              <Dragger {...productImageUploadProps}>
                <p className="ant-upload-drag-icon"><ShopOutlined /></p>
                <p className="ant-upload-text">แนบรูปสินค้า หรืองานบริการ</p>
                <p className="ant-upload-hint">รองรับ JPG และ PNG</p>
              </Dragger>
            </Card>
          </Col>
          <Col xs={24}>
            <Card size="small" title="เอกสารเพิ่มเติม (ถ้ามี)">
              <Dragger {...generalUploadProps}>
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">แนบรูปภาพหรือ PDF เพิ่มเติม</p>
                <p className="ant-upload-hint">สูงสุด 5 ไฟล์ โดยไม่ถูกนำไปแสดงในหน้ารูปแนบอัตโนมัติ</p>
              </Dragger>
            </Card>
          </Col>
        </Row>
        )}
        {uploading && <Progress percent={100} status="active" style={{ marginTop: 8 }} />}
      </Form>
    )
  }

  // Expense Detail Component

  function ExpenseDetail() {
    if (!viewingExpense) return null

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Text strong>เลขที่: </Text>
            <Tag color="blue">{viewingExpense.expense_number}</Tag>
          </Col>
          <Col span={12}>
            <Text strong>วันที่: </Text>
            <Text>{dayjs(viewingExpense.expense_date).format('DD/MM/YYYY')}</Text>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Text strong>หมวดหมู่: </Text>
            <Tag color="green">{viewingExpense.category}</Tag>
          </Col>
          <Col span={12}>
            <Text strong>สถานะ: </Text>
            <Tag color={getStatusColor(viewingExpense.status)}>
              {getStatusText(viewingExpense.status)}
            </Tag>
          </Col>
        </Row>

        <div style={{ marginBottom: 16 }}>
          <Text strong>รายละเอียด: </Text>
          <p>{viewingExpense.description}</p>
        </div>

        {viewingExpense.supplier_name && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Text strong>ผู้จำหน่าย: </Text>
              <Text>{viewingExpense.supplier_name} {viewingExpense.supplier_company && `(${viewingExpense.supplier_company})`}</Text>
            </Col>
          </Row>
        )}

        {viewingExpense.project_name && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Text strong>โครงการ: </Text>
              <Text>{viewingExpense.project_name} {viewingExpense.project_code && `(${viewingExpense.project_code})`}</Text>
            </Col>
          </Row>
        )}

        {viewingExpense.items && viewingExpense.items.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>รายการค่าใช้จ่าย:</Text>
            <Table
              dataSource={viewingExpense.items}
              columns={[
                { title: 'รายละเอียด', dataIndex: 'description', key: 'description' },
                { title: 'จำนวน', dataIndex: 'quantity', key: 'quantity', width: 80 },
                { title: 'ราคาต่อหน่วย', dataIndex: 'unit_price', key: 'unit_price', width: 120, render: (price) => `₿${parseFloat(price).toFixed(2)}` },
                { title: 'รวม', dataIndex: 'total_price', key: 'total_price', width: 120, render: (price) => `₿${parseFloat(price).toFixed(2)}` },
              ]}
              pagination={false}
              size="small"
              style={{ marginTop: 8 }}
            />
          </div>
        )}

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ width: '300px' }}>
                <Row justify="space-between" style={{ marginBottom: 4 }}>
                  <Col><Text>รวมเป็นเงิน:</Text></Col>
                  <Col><Text>₿{parseFloat(viewingExpense.subtotal || viewingExpense.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Col>
                </Row>
                {viewingExpense.discount > 0 && (
                  <Row justify="space-between" style={{ marginBottom: 4 }}>
                    <Col><Text type="danger">ส่วนลด:</Text></Col>
                    <Col><Text type="danger">-₿{parseFloat(viewingExpense.discount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Col>
                  </Row>
                )}
                {viewingExpense.vat_mode !== 'no_tax' && (
                  <Row justify="space-between" style={{ marginBottom: 4 }}>
                    <Col><Text>ภาษีมูลค่าเพิ่ม ({viewingExpense.tax_rate || 7}%):</Text></Col>
                    <Col><Text>₿{parseFloat(viewingExpense.tax_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Col>
                  </Row>
                )}
                <Divider style={{ margin: '8px 0' }} />
                <Row justify="space-between">
                  <Col><Text strong>รวมทั้งสิ้น:</Text></Col>
                  <Col><Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                    ₿{parseFloat(viewingExpense.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text></Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        {viewingExpense.payment_method && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Text strong>วิธีการชำระเงิน: </Text>
              <Text>{paymentMethods.find(m => m.value === viewingExpense.payment_method)?.label || viewingExpense.payment_method}</Text>
            </Col>
          </Row>
        )}

        {viewingExpense.notes && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>หมายเหตุ: </Text>
            <p>{viewingExpense.notes}</p>
          </div>
        )}

        {(viewingExpense.attachments?.length > 0 || viewingExpense.receipt_image) && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>ใบเสร็จ/เอกสาร: </Text>
            <List
              style={{ marginTop: 8 }}
              bordered
              dataSource={viewingExpense.attachments?.length ? viewingExpense.attachments : [{
                original_name: String(viewingExpense.receipt_image).split('/').pop(), url: viewingExpense.receipt_image, legacy: true
              }]}
              renderItem={(file, index) => <List.Item actions={[
                <Button type="link" icon={<EyeOutlined />} onClick={() => setAttachmentPreview(file)}>ดูตัวอย่าง</Button>
              ]}>
                <List.Item.Meta avatar={<FileTextOutlined />} title={file.original_name || `เอกสาร ${index + 1}`}
                  description={`${file.provider === 'google_drive' ? 'Google Drive' : 'Local Storage'}${file.size_bytes ? ` • ${(file.size_bytes / 1024).toFixed(1)} KB` : ''}`} />
              </List.Item>}
            />
          </div>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Text strong>ผู้บันทึก: </Text>
            <Text>{viewingExpense.created_by_name}</Text>
          </Col>
          {viewingExpense.approved_by_name && (
            <Col span={12}>
              <Text strong>ผู้อนุมัติ: </Text>
              <Text>{viewingExpense.approved_by_name}</Text>
            </Col>
          )}
        </Row>
      </div>
    )
  }
}

export default ExpensesPage
